// src/pages/HospitalCourse.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import Header from "../components/Header";
import jsPDF from "jspdf";
import {
  ArrowLeft,
  CheckSquare,
  Square,
  Sparkles,
  Save,
  Copy,
  FileDown,
  CheckCircle,
  Wand2,
} from "lucide-react";

import lumosyeLogo from "../assets/Lumosye-logo.png";
import lumochartLogo from "../assets/lumochart-login.png";

import { apiPost } from "../utils/api"; // ✅ unified backend helper

// -----------------------------------------------------
// Selectable Note Item
// -----------------------------------------------------
const NoteSelectItem = ({ note, isSelected, onToggle }) => {
  const noteTypeStyles = {
    Admission: "border-blue-500",
    Progress: "border-teal-500",
    Discharge: "border-green-500",
    Consult: "border-purple-500",
    Default: "border-gray-500",
  };

  const formatDate = (ts) => {
    if (!ts) return "No date";
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
      return d.toLocaleDateString();
    } catch {
      return "No date";
    }
  };

  return (
    <div
      onClick={onToggle}
      className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer border-l-4 transition-colors ${
        isSelected
          ? "bg-blue-50 dark:bg-blue-900/50"
          : "hover:bg-slate-50 dark:hover:bg-zinc-800/50"
      } ${noteTypeStyles[note.type] || noteTypeStyles.Default}`}
    >
      {isSelected ? (
        <CheckSquare className="text-blue-600" />
      ) : (
        <Square className="text-gray-400" />
      )}

      <div>
        <p className="font-semibold text-gray-800 dark:text-white">
          {note.type || "Note"}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(note.timestamp)}
        </p>
      </div>
    </div>
  );
};

// -----------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------
export default function HospitalCourse() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { profile } = useUserProfile();

  const [patient, setPatient] = useState(null);
  const [allNotes, setAllNotes] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState(new Set());
  const [generatedCourse, setGeneratedCourse] = useState("");
  const [existingCourseNoteId, setExistingCourseNoteId] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // -----------------------------------------------------
  // Fetch patient + notes
  // -----------------------------------------------------
  const fetchData = useCallback(async () => {
    if (!patientId) return;

    setIsLoading(true);
    setErrorMsg("");

    try {
      const patientRef = doc(db, "patients", patientId);
      const patientSnap = await getDoc(patientRef);

      if (patientSnap.exists()) setPatient(patientSnap.data());

      const notesQuery = query(
        collection(db, "notes"),
        where("patientID", "==", patientId),
        orderBy("timestamp", "asc")
      );

      const notesSnapshot = await getDocs(notesQuery);

      const notes = [];
      let existingCourse = null;

      notesSnapshot.docs.forEach((d) => {
        const n = { id: d.id, ...d.data() };
        if (n.type === "Hospital Course") {
          existingCourse = n;
        } else {
          notes.push(n);
        }
      });

      setAllNotes(notes);
      setSelectedNoteIds(new Set(notes.map((n) => n.id)));

      if (existingCourse) {
        setExistingCourseNoteId(existingCourse.id);
        setGeneratedCourse(existingCourse.summary || "");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setErrorMsg(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // -----------------------------------------------------
  // Select / Deselect notes
  // -----------------------------------------------------
  const handleToggleNote = (id) => {
    setSelectedNoteIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const handleToggleAll = () => {
    if (selectedNoteIds.size === allNotes.length)
      setSelectedNoteIds(new Set());
    else setSelectedNoteIds(new Set(allNotes.map((n) => n.id)));
  };

  // -----------------------------------------------------
  // Generate Hospital Course → /v1/generate-hospital-summary
  // -----------------------------------------------------
  const handleGenerateCourse = async () => {
    setIsGenerating(true);
    setErrorMsg("");

    const selectedNotes = allNotes.filter((n) => selectedNoteIds.has(n.id));

    try {
      const payload = {
        notes: selectedNotes.map((n) => ({
          timestamp:
            n.timestamp?.toDate?.().toISOString?.() ||
            new Date().toISOString(),
          type: n.type || "Note",
          hpi: n.hpi || "",
          subjective: n.subjective || "",
          objective: n.objective || "",
          exam: n.exam || "",
          ap: n.ap || n.summary || "",
        })),
      };

      const data = await apiPost("/generate-hospital-summary", payload);

      setGeneratedCourse(data.summary || "");
    } catch (err) {
      console.error("Generate error:", err);
      setGeneratedCourse(`Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // -----------------------------------------------------
  // Refine text → /v1/refine-text
  // -----------------------------------------------------
  const handleRefineCourse = async () => {
    if (!generatedCourse.trim()) return;

    setIsGenerating(true);
    setErrorMsg("");

    try {
      const data = await apiPost("/refine-text", {
        text: generatedCourse,
      });

      setGeneratedCourse(data.text || generatedCourse);
    } catch (err) {
      console.error("Refine error:", err);
      setErrorMsg(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // -----------------------------------------------------
  // Save hospital course as note
  // -----------------------------------------------------
  const handleSaveCourse = async () => {
    if (!generatedCourse.trim()) return;

    setIsSaving(true);
    setIsSaved(false);

    const payload = {
      ownerId: currentUser.uid,
      patientID: patientId,
      type: "Hospital Course",
      summary: generatedCourse,
      timestamp: serverTimestamp(),
    };

    try {
      if (existingCourseNoteId) {
        await setDoc(doc(db, "notes", existingCourseNoteId), payload, {
          merge: true,
        });
      } else {
        const ref = await addDoc(collection(db, "notes"), payload);
        setExistingCourseNoteId(ref.id);
      }

      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 1500);
    } catch (err) {
      console.error("Save error:", err);
      setErrorMsg(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // -----------------------------------------------------
  // Export PDF
  // -----------------------------------------------------
  const exportCourseAsPDF = () => {
    if (!generatedCourse || !profile || !patient)
      return alert("Missing data.");

    const fullName = `${patient.firstName || ""} ${
      patient.lastName || ""
    }`.trim();

    const pdf = new jsPDF("p", "pt", "letter");
    const m = 40;
    const width = pdf.internal.pageSize.getWidth();
    let y = m;

    // Logos
    const r = 3226 / 825;
    const logoW = 100;
    const logoH = logoW / r;
    pdf.addImage(lumosyeLogo, "PNG", m, y, logoW, logoH);

    pdf.addImage(lumochartLogo, "PNG", width - m - 40, y, 40, 40);
    y += Math.max(logoH, 40) + 20;

    pdf.setDrawColor(220);
    pdf.line(m, y, width - m, y);
    y += 25;

    pdf.setFontSize(20).setFont("helvetica", "bold");
    pdf.text("Hospital Course Summary", width / 2, y, { align: "center" });
    y += 30;

    pdf.setFontSize(12).setFont("helvetica", "normal");
    pdf.text(`Patient: ${fullName}`, m, y);
    pdf.text(
      `Date: ${new Date().toLocaleString()}`,
      width - m - 160,
      y
    );
    y += 30;

    const lines = pdf.splitTextToSize(generatedCourse, width - m * 2);
    pdf.text(lines, m, y);

    pdf.save(`LumoChart_HospitalCourse_${patient.lastName}.pdf`);
  };

  // -----------------------------------------------------
  // Clipboard copy
  // -----------------------------------------------------
  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCourse);
    alert("Copied to clipboard!");
  };

  // -----------------------------------------------------
  // UI
  // -----------------------------------------------------
  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />

      <main className="max-w-5xl mx-auto py-10 px-4">
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800 mr-4"
          >
            <ArrowLeft size={20} />
          </button>

          <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">
            Hospital Course Summary
          </h1>
        </div>

        {errorMsg && (
          <p className="text-red-500 text-sm mb-4">{errorMsg}</p>
        )}

        {isLoading ? (
          <div className="text-center p-10 text-gray-500">
            Loading notes...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* LEFT COLUMN */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6 space-y-4">
              <div className="flex justify-between">
                <h2 className="text-xl font-bold">Select Notes</h2>
                <button
                  onClick={handleToggleAll}
                  className="text-sm text-blue-600"
                >
                  {selectedNoteIds.size === allNotes.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {allNotes.map((note) => (
                  <NoteSelectItem
                    key={note.id}
                    note={note}
                    isSelected={selectedNoteIds.has(note.id)}
                    onToggle={() => handleToggleNote(note.id)}
                  />
                ))}
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="lg:col-span-2 space-y-8">
              {/* Generate Box */}
              <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6 space-y-4">
                <h2 className="text-xl font-bold">
                  Generate Summary
                </h2>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleGenerateCourse}
                    disabled={isGenerating || selectedNoteIds.size === 0}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-full font-semibold hover:bg-purple-600 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Sparkles size={16} /> Generate
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleRefineCourse}
                    disabled={!generatedCourse || isGenerating}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Wand2 size={16} /> Refine
                      </>
                    )}
                  </button>
                </div>

                <textarea
                  value={generatedCourse}
                  onChange={(e) => setGeneratedCourse(e.target.value)}
                  placeholder="AI-generated hospital course will appear here..."
                  className="w-full h-80 bg-slate-100 dark:bg-zinc-800 rounded-lg p-4 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Export & Save */}
              <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm p-6">
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={handleCopy}
                    disabled={!generatedCourse}
                    className="py-2 bg-slate-200 dark:bg-zinc-800 rounded-lg hover:bg-slate-300 dark:hover:bg-zinc-700"
                  >
                    <Copy size={16} />
                  </button>

                  <button
                    onClick={exportCourseAsPDF}
                    disabled={!generatedCourse}
                    className="py-2 bg-slate-200 dark:bg-zinc-800 rounded-lg hover:bg-slate-300 dark:hover:bg-zinc-700"
                  >
                    <FileDown size={16} />
                  </button>

                  <button
                    onClick={handleSaveCourse}
                    disabled={!generatedCourse || isSaving || isSaved}
                    className={`py-2 rounded-lg text-white font-semibold ${
                      isSaved
                        ? "bg-green-500"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                  >
                    {isSaving ? (
                      <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                    ) : isSaved ? (
                      <CheckCircle size={16} />
                    ) : (
                      <Save size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
